import { colors2, TextInput, ButtonPrimaryV2, UI_HEADLINE_SM } from 'oolib';
import React, { useState } from 'react';
import styled from 'styled-components';
import CorrelationHeatmapContainer from './CorrelationHeatmapContainer';
import { SidebarLayoutV2 } from '@/components/layout/SidebarLayoutV2';
import { useQuery } from 'react-query';
import axios from 'axios';
import { DataVizAccordion } from '@/components/dataviz/DataVizAccordion';

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  align-items: flex-end;
`;

const ResultsContainer = styled.div`
  margin-top: 20px;
`;

const ErrorMessage = styled.div`
  color: red;
  background: #ffeeee;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const Summary = styled.div`
  background: #f5f5f5;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  line-height: 1.5;
`;

/**
 * Analyzes data structure and returns appropriate DataVizAccordion configuration
 * @param {Object} data - The data object containing visualization info
 * @returns {Object} - Configuration for DataVizAccordion component
 */
const getDataVizConfig = (data) => {
  // Default configuration
  const defaultProps = {
    title: data.title || "Data Visualization",
    data: [],
    valuePath: "count",
    labelPath: "display",
    tooltipLabelsMapping: {},
    chartOptions: [
      {
        display: "Bar Chart",
        comp: "BarChart",
        props: {}
      }
    ]
  };

  if (!data || !data.data || !Array.isArray(data.data) || data.data.length === 0) {
    console.warn("Invalid or empty data provided");
    return {
      ...defaultProps
    }
  }

  // Handle different tools
  switch (data.toolUsed) {
    case "AggregateMetricsAcrossDimensionsTool":
      return handleAggregateMetrics(data, defaultProps);
    case "CorrelationHeatmapTool":
      // Reserved for future implementation
      return {
        ...defaultProps,
        component: "CorrelationHeatmap",
      };
    // Add other tools as needed
    default:
      console.warn(`Unknown tool type: ${data.toolUsed}`);
      return defaultProps;
  }
};

/**
 * Analyzes nesting level of data array
 * @param {Array} dataArray - The data array to analyze
 * @returns {Object} - Information about nesting levels
 */
const analyzeDataStructure = (dataArray) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return { nestingLevel: 0, hasMultipleTopItems: false };
  }

  const hasMultipleTopItems = dataArray.length > 1;
  let maxNestingLevel = 0;

  // Check for children in each top-level item
  for (const item of dataArray) {
    if (item.children && Array.isArray(item.children) && item.children.length > 0) {
      maxNestingLevel = 1;
      
      // Check for grandchildren (2nd level nesting)
      for (const child of item.children) {
        if (child.children && Array.isArray(child.children) && child.children.length > 0) {
          maxNestingLevel = 2;
          break;
        }
      }
      
      if (maxNestingLevel === 2) break; // No need to check further
    }
  }

  return { 
    nestingLevel: maxNestingLevel, 
    hasMultipleTopItems 
  };
};

/**
 * Handles AggregateMetricsAcrossDimensionsTool data configuration
 * @param {Object} data - The data object
 * @param {Object} defaultProps - Default configuration
 * @returns {Object} - Configured props for DataVizAccordion
 */
const handleAggregateMetrics = (data, defaultProps) => {
  const dataArray = data.data;
  const { nestingLevel, hasMultipleTopItems } = analyzeDataStructure(dataArray);
  
  // Case 1: No children arrays, main array has multiple items
  if (nestingLevel === 0 && hasMultipleTopItems) {
    return {
      ...defaultProps,
      title: data.title,
      data: dataArray,
      chartOptions: [
        {
          display: "Bar Chart",
          comp: "BarChart",
          props: {}
        }
      ]
    };
  }
  
  // Case 2: Main array has only 1 object with children
if (nestingLevel >= 1 && !hasMultipleTopItems) {
  // Check if the children themselves have nested structure
  const childrenHaveNesting = dataArray[0].children && 
    dataArray[0].children.some(child => 
      child.children && Array.isArray(child.children) && child.children.length > 0
    );
  
  if (childrenHaveNesting) {
    // Similar to Case 3, create grouped/stacked visualization for children & their sub-children
    const uniqueSubCategories = new Set();
    dataArray[0].children.forEach(child => {
      if (child.children && Array.isArray(child.children)) {
        child.children.forEach(subChild => {
          uniqueSubCategories.add(subChild.value);
        });
      }
    });
    
    const valuePathsArray = Array.from(uniqueSubCategories).map(category => {
      return `${category}.count`;
    });
    
    const tooltipLabelsMapping = {};
    dataArray[0].children.forEach(child => {
      if (child.children && Array.isArray(child.children)) {
        child.children.forEach(subChild => {
          tooltipLabelsMapping[`${subChild.value}.count`] = subChild.display;
        });
      }
    });
    
    const transformedData = dataArray[0].children.map(child => {
      const result = {
        display: child.display,
        value: child.value,
        ...child
      };
      
      if (child.children && Array.isArray(child.children)) {
        child.children.forEach(subChild => {
          result[subChild.value] = {
            count: subChild.count,
            display: subChild.display
          };
        });
      }
      
      return result;
    });
    
    return {
      ...defaultProps,
      title: data.title,
      data: transformedData,
      valuePath: valuePathsArray,
      tooltipLabelsMapping: tooltipLabelsMapping,
      chartOptions: [
        {
          display: "Grouped Bar Chart",
          comp: "BarChart",
          props: {
            breakdownDisplayType: "grouped"
          }
        },
        {
          display: "Stacked Bar Chart",
          comp: "BarChart",
          props: {
            breakdownDisplayType: "stacked"
          }
        }
      ]
    };
  } else {
    // Original Case 2 - simple bar chart for children when they don't have sub-children
    return {
      ...defaultProps,
      title: data.title,
      data: dataArray[0].children,
      chartOptions: [
        {
          display: "Bar Chart",
          comp: "BarChart",
          props: {}
        }
      ]
    };
  }
}
  
  // Case 3: Main array has multiple objects with children - Grouped Bar Chart
  if (nestingLevel >= 1 && hasMultipleTopItems) {
    // Prepare data for grouped bar chart
    // Extract all unique child categories across all parent items
    const uniqueCategories = new Set();
    dataArray.forEach(item => {
      if (item.children && Array.isArray(item.children)) {
        item.children.forEach(child => {
          uniqueCategories.add(child.value);
        });
      }
    });
    
    // Create valuePath array for each unique category
    const valuePathsArray = Array.from(uniqueCategories).map(category => {
      return `${category}.count`;
    });
    
    // Create tooltipLabelsMapping
    const tooltipLabelsMapping = {};
    dataArray.forEach(item => {
      if (item.children && Array.isArray(item.children)) {
        item.children.forEach(child => {
          tooltipLabelsMapping[`${child.value}.count`] = child.display;
        });
      }
    });
    
    // Transform data structure for grouped bar chart
    const transformedData = dataArray.map(item => {
      const result = {
        display: item.display,
        value: item.value,
        // Add other top-level properties
        ...item
      };
      
      // Add each child as a property
      if (item.children && Array.isArray(item.children)) {
        item.children.forEach(child => {
          result[child.value] = {
            count: child.count,
            display: child.display
          };
        });
      }
      
      return result;
    });

    return {
      ...defaultProps,
      title: data.title,
      data: transformedData,
      valuePath: valuePathsArray,
      tooltipLabelsMapping: tooltipLabelsMapping,
      chartOptions: [
        {
          display: "Grouped Bar Chart",
          comp: "BarChart",
          props: {
            breakdownDisplayType: "grouped"
          }
        },
        {
          display: "Stacked Bar Chart",
          comp: "BarChart",
          props: {
            breakdownDisplayType: "stacked"
          }
        }
      ]
    };
  }
  
  // Default fallback
  return {
    ...defaultProps,
    title: data.title
  };
};

export const Common = () => {
 
  return (
 <SidebarLayoutV2
    gridHeightCSS='100vh'
    lsbComp={<div>hello there hello therehello therehello therehello therehello therehello there</div>}
    lsbAllowCollapse
    rsbComp={<div>hello there hello therehello therehello therehello therehello therehello there</div>}
    rsbAllowCollapse
    rsbAllowResize
    lsbAllowResize
  />
  );
};


